import { useEffect, useRef, useState } from 'react';
import type { ConfigResponse, MeResponse } from '../shared/api.ts';
import type { User } from '../shared/events.ts';
import { Orb } from './canvas/Orb.tsx';
import { api } from './lib/api.ts';

type Gis = {
  accounts: {
    id: {
      initialize(opts: {
        client_id: string;
        callback: (r: { credential: string }) => void;
        hd?: string;
        ux_mode?: string;
      }): void;
      renderButton(el: HTMLElement, opts: Record<string, unknown>): void;
    };
  };
};

function loadGis(): Promise<Gis> {
  const w = window as unknown as { google?: Gis };
  if (w.google?.accounts) return Promise.resolve(w.google);
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.onload = () => (w.google ? resolve(w.google) : reject(new Error('Google sign-in failed to load')));
    s.onerror = () => reject(new Error('Google sign-in failed to load'));
    document.head.appendChild(s);
  });
}

export function SignIn({
  config,
  onUser,
  signInGoogle = api.signInGoogle,
}: {
  config: ConfigResponse;
  onUser: (u: User, isAdmin: boolean, flags: MeResponse['flags']) => void;
  /** The Lab passes a no-op, so its Google button can never create a session. */
  signInGoogle?: (credential: string) => Promise<MeResponse>;
}) {
  const button = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('akshat@stimuler.tech');

  useEffect(() => {
    if (config.authMode !== 'google' || !config.googleClientId || !button.current) return;
    const el = button.current;
    loadGis()
      .then((gis) => {
        gis.accounts.id.initialize({
          client_id: config.googleClientId!,
          hd: 'stimuler.tech',
          callback: ({ credential }) =>
            signInGoogle(credential)
              .then((r: MeResponse) => r.user && onUser(r.user, r.isAdmin, r.flags))
              .catch((e: Error) => setError(e.message)),
        });
        gis.accounts.id.renderButton(el, {
          // Google's own button styles: outlined on the light theme, filled on dark.
          theme: document.documentElement.dataset.theme === 'dark' ? 'filled_black' : 'outline',
          size: 'large',
          shape: 'pill',
          text: 'continue_with',
        });
      })
      .catch((e: Error) => setError(e.message));
  }, [config, onUser, signInGoogle]);

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-8 px-6 text-center">
      <Orb state="idle" />
      <div className="space-y-2">
        <h1 className="text-4xl font-semibold tracking-tight text-fg">fuse</h1>
        <p className="text-subtle">Your interview coach that talks back. Stimuler team only.</p>
      </div>
      {config.authMode === 'google' ? (
        // Google's button is a light-scheme iframe. Under a dark color-scheme (the Lab's dark theme) Chrome paints an
        // opaque white canvas behind it; a light scheme on its container keeps the iframe transparent.
        <div ref={button} data-testid="google-signin" className="min-h-11" style={{ colorScheme: 'light' }} />
      ) : (
        <form
          className="flex w-full max-w-sm gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            api
              .signInDev(email)
              .then((r) => r.user && onUser(r.user, r.isAdmin, r.flags))
              .catch((err: Error) => setError(err.message));
          }}
        >
          <input
            data-testid="dev-email"
            aria-label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="min-w-0 flex-1 rounded-full bg-fg/5 px-4 py-2.5 text-fg ring-1 ring-fg/10 focus:outline-none"
          />
          <button type="submit" data-testid="dev-signin" className="rounded-full bg-fg px-5 font-semibold text-bg">
            Dev sign in
          </button>
        </form>
      )}
      {error ? (
        <p role="alert" className="text-sm text-bad">
          {error}
        </p>
      ) : null}
    </div>
  );
}
